/**
 * Performance, Rebates / Reimbursement tab: the facility reimbursement portal
 * (src/financial/reimbursement-portal.ts, served by src/financial/router.ts) and its
 * routes. All fields and routes here are served; the Performance extras (eligible, mode,
 * blockedReason, claimId, bases, payerMix, readinessTotals, monthly, pace, partAFeed,
 * ytdCapturedNet) and the portfolio roll-up landed with be-money performance.
 */
import { route } from './route';

/** The honesty word the tab prints beside a figure: OURS (our billing record), MODELED, PART MODEL. */
export type RebatesBasis = 'measured' | 'modeled' | 'part_modeled';

export interface PortalLeakage {
  capturedMonthly: number;
  capturedNote: string;
  capturableMonthly: number;
  leakageMonthly: number;
  leakagePct: number;
  captureRate: number;
  capturableNetMonthly: number;
  capturedNetMonthly: number;
  leakageNetMonthly: number;
  leakageNetPct: number;
  captureRateNet: number;
  payerRecorded: number | null;
  lensNote: string;
  residents: number;
  partASuppressed: number;
  real: boolean;
  /** False while no census feed tells Part A stays apart (stays are marked by hand today). */
  partAFeed?: boolean;
  /** Captured, collectible, year to date (by payer rule until remits land); null with no building. */
  ytdCapturedNet?: number | null;
}

export interface PortalPipeline {
  /** opportunity | readiness | enroll | submit | paid */
  stages: { key: string; label: string; residents: number }[];
  needAction: { key: string; label: string; residents: number }[];
  real: boolean;
}

export interface PortalLiveProgram {
  status: 'live';
  code: string;
  category: string;
  label: string;
  lead?: boolean;
  rate: number;
  rateVerified: boolean;
  enrolled: number;
  monthly: number;
  netMonthly: number;
  baselineMonthly?: number;
  createdMonthly?: number;
  recoveredMonthly?: number;
  censusNormalized?: boolean;
  /** Residents eligible for the code (enrolled or not); null for a code the engine does not evaluate (APCM, CoCM). */
  eligible?: number | null;
  /** Who does the work: Aithora captures (drive), the facility performs (detect), or no rail (none). */
  mode?: 'drive' | 'detect' | 'none';
  /** Why a code cannot bill here ("No paired device ..."), or null. */
  blockedReason?: string | null;
}

export interface PortalBuildingProgram {
  status: 'building';
  key: string;
  label: string;
  statusLabel: string;
  note: string;
  estimateAnnualUpTo?: number | null;
  poolName?: string | null;
}

export interface PortalReadinessRow {
  residentId: string;
  resident: string;
  blocker: string;
  blockerLabel: string;
  action: string;
  actionRoute: string;
  blockedMonthly: number;
  blockedNetMonthly: number;
  /** The resident's open CCM claim this period, so the button can record the fix through /capture/actions; null when none exists. */
  claimId?: string | null;
}

export interface PortalClaims {
  stages: { key: string; label: string; count: number; amount: number }[];
  total: { count: number; amount: number };
  pendingAmount: number;
  denials: { residentId: string; resident: string; code: string; reason: string; reworkHint: string }[];
  auditBundleAvailable: boolean;
  real: boolean;
}

export interface PortalAttribution {
  baselineMonthly: number;
  baselineMonthlyAllCodes: number;
  createdMonthly: number;
  recoveredMonthly: number;
  liftMonthly: number;
  baselineMonths: number;
  baselineCodes: number;
  censusNormalized: boolean;
}

/** GET /api/financial/facilities/mine/reimbursement-portal and /facilities/:id/reimbursement-portal */
export interface ReimbursementPortal {
  organizationId: string | null;
  period: string;
  primaryCode: string;
  leakage: PortalLeakage;
  pipeline: PortalPipeline;
  programs: { live: PortalLiveProgram[]; building: PortalBuildingProgram[] };
  readiness: PortalReadinessRow[];
  claims: PortalClaims;
  caveats: string[];
  generatedAt: string;
  goLiveDate: string | null;
  historyIngested: boolean;
  attribution: PortalAttribution | null;
  /** Each headline figure's basis, so the tab can say OURS, MODELED or PART MODEL. */
  bases?: Partial<Record<'attribution' | 'leakage' | 'capturable' | 'captureRate' | 'perResident' | 'ytd', RebatesBasis>>;
  /** The payer mix of residents with a payer on file, as shares 0 to 1 (dual counts as Medicare, Medicare Advantage as private); null when none is recorded. */
  payerMix?: { medicare: number; medicaid: number; private: number } | null;
  /** Residents on the readiness worklist and the gross dollars a month they hold. */
  readinessTotals?: { residents: number; blockedMonthly: number } | null;
  /** Collectible booked per month from go-live (or the first claim), oldest first ("2026-05"), at most 36; null with neither. */
  monthly?: { period: string; bookedMonthly: number }[] | null;
  /** Onboarding pace. Denial rate stays null until a claim is adjudicated. */
  pace?: { daysToFirstClaim: number | null; enrollmentsPerWeek: number | null; denialRate: number | null } | null;
  /** Portfolio roll-up only: how many buildings were summed. */
  facilities?: number;
}

type Q = { period?: string };
/** GET /api/financial/facilities/mine/reimbursement-portal: a Facility Admin's own building. */
export const myReimbursementPortal = route<ReimbursementPortal, Q>()('GET', '/api/financial/facilities/mine/reimbursement-portal');
/** GET /api/financial/facilities/:id/reimbursement-portal: one building for an operator (names masked). */
export const facilityReimbursementPortal = route<ReimbursementPortal, Q>()('GET', '/api/financial/facilities/:id/reimbursement-portal');
/** GET /api/financial/facilities/portfolio/reimbursement-portal: the operator's roll-up across buildings (operator only, names masked). */
export const portfolioReimbursementPortal = route<ReimbursementPortal, Q>()('GET', '/api/financial/facilities/portfolio/reimbursement-portal');

export const performanceRebatesApi = { myReimbursementPortal, facilityReimbursementPortal, portfolioReimbursementPortal } as const;
